import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

export const Route = createFileRoute("/demo/")({
  head: () => ({
    meta: [
      { title: "Demo stron — SkuteczneStrony" },
      {
        name: "description",
        content:
          "Zobacz przykładowe demo stron internetowych: restauracja, kancelaria, studio fitness, salon kosmetyczny, sklep e-commerce i więcej. Wybierz branżę i zobacz Twoją stronę za 72h.",
      },
      { property: "og:title", content: "Demo stron — SkuteczneStrony" },
      {
        property: "og:description",
        content:
          "Przykładowe realizacje stron internetowych dla różnych branż. Wybierz demo i porozmawiajmy o Twojej stronie.",
      },
    ],
  }),
  component: DemoPage,
});

type Demo = {
  slug: string;
  branch: string;
  title: string;
  desc: string;
  tags: string[];
  gradient: string;
  accent: string;
  available: boolean;
};

const DEMOS: Demo[] = [
  {
    slug: "elektryk",
    branch: "Usługi techniczne",
    title: "Elektryk — Volt & Co.",
    desc: "Strona dla elektryka z cennikiem usług, obszarem działania, formularzem zgłoszenia awarii i opiniami klientów.",
    tags: ["Cennik usług", "Awarie 24/7", "Opinie Google"],
    gradient: "from-[#FACC15] to-[#F97316]",
    accent: "#FACC15",
    available: true,
  },
  {
    slug: "burze-stanica",
    branch: "Live data",
    title: "Burze · Stanica — nowcast",
    desc: "Prognoza burzowa live dla Stanicy i okolic Gliwic. Trzy modele numeryczne (ICON-D2, ICON-EU, ECMWF IFS) + autorska formuła ryzyka konwekcyjnego.",
    tags: ["ICON-D2", "ICON-EU", "ECMWF", "Risk 0-100"],
    gradient: "from-[#06B6D4] to-[#A21CAF]",
    accent: "#22D3EE",
    available: true,
  },
  {
    slug: "ecommerce",
    branch: "E-commerce",
    title: "Sklep Bloom & Co.",
    desc: "Sklep internetowy z koszykiem, płatnościami online, integracją z kurierami i panelem zarządzania zamówieniami.",
    tags: ["Płatności", "Koszyk", "Kurierzy"],
    gradient: "from-[#00F5A0] to-[#00D9F5]",
    accent: "#00F5A0",
    available: false,
  },
  {
    slug: "deweloper",
    branch: "Nieruchomości",
    title: "Deweloper Horizon Estate",
    desc: "Prezentacja inwestycji deweloperskiej z wirtualnym spacerem, interaktywnym wyborem mieszkań i kalkulatorem rat.",
    tags: ["Wybór mieszkań", "Kalkulator", "Galeria 3D"],
    gradient: "from-[#6366F1] to-[#8B5CF6]",
    accent: "#6366F1",
    available: false,
  },
];

function DemoPage() {
  return (
    <main className="relative min-h-screen overflow-hidden bg-[#030508] text-white">
      {/* Ambient gradients */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 top-0 h-[500px] w-[500px] rounded-full bg-[#00F5A0]/10 blur-[120px]" />
        <div className="absolute -right-32 top-1/3 h-[500px] w-[500px] rounded-full bg-[#6366F1]/10 blur-[120px]" />
      </div>

      {/* Back link */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 pt-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-slate-300 backdrop-blur-xl transition hover:border-white/20 hover:text-white"
        >
          <ArrowLeft size={14} />
          Strona główna
        </Link>
      </div>

      {/* Header */}
      <header className="relative z-10 mx-auto max-w-7xl px-6 pb-12 pt-12 text-center md:pt-20">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-xs font-medium text-slate-300 backdrop-blur-xl">
          <Sparkles size={12} className="text-[#00F5A0]" />
          Galeria demo realizacji
        </div>
        <h1 className="mx-auto max-w-4xl text-balance text-[10vw] font-black leading-[0.95] tracking-tight sm:text-6xl md:text-7xl">
          Wybierz{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage: "linear-gradient(110deg,#00F5A0 0%,#6366F1 100%)",
            }}
          >
            demo
          </span>{" "}
          dla swojej branży
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
          Każde demo to gotowy punkt startowy. Kliknij, zobacz jak działa, a my w 72h
          dostosujemy je do Twojej marki — bez zaliczki, bez ryzyka.
        </p>
      </header>

      {/* Grid */}
      <section className="relative z-10 mx-auto max-w-7xl px-6 pb-32">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {DEMOS.map((d) => (
            <DemoCard key={d.slug} demo={d} />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-20 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-8 backdrop-blur-xl md:p-12">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="max-w-xl">
              <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
                Nie widzisz swojej branży?
              </h2>
              <p className="mt-2 text-sm text-slate-400 md:text-base">
                Tworzymy dedykowane demo pod Twoją działalność — całkowicie za darmo,
                w 72 godziny. Bez zobowiązań.
              </p>
            </div>
            <Link
              to="/"
              hash="cta"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00F5A0] to-[#6366F1] px-6 py-3 text-sm font-black text-black shadow-lg shadow-[#00F5A0]/30 transition hover:brightness-110"
            >
              Zamów własne demo
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function DemoCard({ demo }: { demo: Demo }) {
  const inner = (
    <article
      className={`group relative h-full overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl transition-all duration-500 ${
        demo.available
          ? "hover:-translate-y-2 hover:border-white/30 hover:bg-white/[0.04] hover:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] cursor-pointer"
          : "opacity-70"
      }`}
    >
      {/* Preview area */}
      <div className={`relative h-48 overflow-hidden bg-gradient-to-br ${demo.gradient} transition-transform duration-700 group-hover:scale-[1.04]`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.25),transparent_55%)]" />
        {/* shimmer sweep */}
        {demo.available && (
          <div className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
        )}
        <div className="absolute inset-x-6 bottom-6 flex items-end justify-between">
          <div className="rounded-full bg-black/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white backdrop-blur-md">
            {demo.branch}
          </div>
          <div className="h-10 w-10 rounded-full bg-white/20 backdrop-blur-md ring-1 ring-white/30" />
        </div>
        {/* mock browser bar */}
        <div className="absolute inset-x-4 top-4 flex h-6 items-center gap-1.5 rounded-md bg-black/30 px-3 backdrop-blur-md">
          <span className="h-2 w-2 rounded-full bg-white/40" />
          <span className="h-2 w-2 rounded-full bg-white/40" />
          <span className="h-2 w-2 rounded-full bg-white/40" />
        </div>
      </div>

      {/* Body */}
      <div className="p-6">
        <h3 className="text-lg font-bold tracking-tight text-white">{demo.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">{demo.desc}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {demo.tags.map((t) => (
            <span
              key={t}
              className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] font-medium text-slate-300"
            >
              {t}
            </span>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          {demo.available ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#00F5A0]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00F5A0] opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00F5A0]" />
              </span>
              Dostępne — zobacz na żywo
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <span className="h-2 w-2 rounded-full bg-slate-600" />
              W przygotowaniu
            </span>
          )}
          <span
            className="inline-flex items-center gap-1 text-sm font-semibold transition-all duration-300 group-hover:gap-2"
            style={{ color: demo.accent }}
          >
            {demo.available ? "Otwórz demo" : "Wkrótce"}
            <ArrowRight
              size={14}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </span>
        </div>
      </div>

      {/* hover ring */}
      <div
        className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 ring-1 ring-inset transition-opacity duration-500 group-hover:opacity-100"
        style={{ boxShadow: `inset 0 0 60px -20px ${demo.accent}40` }}
      />
    </article>
  );

  if (!demo.available) return inner;

  const targetMap: Record<string, string> = {
    elektryk: "/demo/elektryk",
    "burze-stanica": "/demo/burze-stanica",
  };
  const to = targetMap[demo.slug];
  if (to) {
    return (
      <Link
        to={to}
        className="block rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
      >
        {inner}
      </Link>
    );
  }
  return inner;
}